import type { DtcCodeEntity } from '@packages/domain/dtc-codes';
import type { RetailerProfileEntity } from '@packages/domain/retailers';
import type { TechlineAssetEntity } from '@packages/domain/techline/assets';
import type { TechlineCaseEntity } from '@packages/domain/techline/cases';
import type { VinDetailEntity } from '@packages/domain/vin';
import type { FetchTitlesParams, WorksheetCaseGroupEntity } from '@packages/domain/worksheets/groups';

import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { getLogger } from '@packages/core/logging';
import { colors } from '@packages/core/styles';
import { AlertBar, Typography } from '@packages/ui/shared';
import { analyticsService } from '@web/services/singletons';
import { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { WorksheetGroup, WorksheetOpenMode } from '../../worksheet-group-accordion';

import { signalDashboardAndCloseTab } from '../../../utils/case-tab-sync';
import { type DraftStepperValidation } from '../../hooks/use-draft-stepper-validation';
import { TechlineHeader } from '../../techline-header';
import { BackModal } from './back-modal';
import { DeleteCaseModal } from './delete-case-modal';
import { DraftSidebar } from './draft-sidebar/draft-sidebar';
import { SubmitCaseModal } from './submit-case-modal';
import { TechlineDraftStepper } from './techline-draft-stepper/techline-draft-stepper';

const logger = getLogger('techline');

/** Props for the DraftCaseSubview component. */
export type DraftCaseSubviewProps = {
    /** Error message from loading available worksheet groups. */
    availableGroupError: string;

    /** All available worksheet groups with their titles, for the worksheets step. */
    availableWorksheetGroupData: WorksheetGroup[];

    /** Case-level file attachments. */
    assetData: TechlineAssetEntity[];

    /** Error message from a failed asset upload or processing. Empty when no error. */
    assetErrorMessage: string;

    /** The case details for the current draft case. */
    caseDetails: TechlineCaseEntity;

    /** The case number being edited. */
    caseNumber: string;

    /** Whether the current user is allowed to edit this draft. */
    canEditDraft: boolean;

    /** Validation state for the draft stepper, managed by useDraftStepperValidation. */
    draftStepperValidation: DraftStepperValidation;

    /** Callback to add a team member by username. */
    onAddTeamMember: (username: string) => Promise<boolean>;

    /** Dismiss the asset error alert bar. */
    onAssetErrorDismiss: () => void;

    /** Callback when case details are updated. */
    onCaseDetailsChange: (data: Partial<TechlineCaseEntity>) => void;

    /** Callback to delete an asset by ID. */
    onDeleteAsset: (assetId: string) => Promise<void>;

    /** Callback to delete the current draft case. */
    onDeleteCase: () => Promise<boolean>;

    /** Callback to delete a draft worksheet submission. */
    onDeleteDraftWorksheetSubmission: (submissionId: string) => Promise<boolean>;

    /** Callback to fetch worksheet titles for a group. */
    onFetchWorksheetTitles: (params: FetchTitlesParams) => Promise<WorksheetCaseGroupEntity[] | undefined>;

    /** Callback to open a worksheet (new or existing). */
    onOpenWorksheet?: (worksheetId: string, submissionId: string | undefined, mode: WorksheetOpenMode) => void;

    /** Callback to refresh the asset list. */
    onRefreshAssets: () => Promise<void>;

    /** Callback to save the current draft case. */
    onSaveCase: () => Promise<boolean>;

    /** Callback to submit the current case for review. */
    onSubmitCase: () => Promise<boolean>;

    /** Callback to update an existing asset. */
    onUpdateAsset: (assetId: string, update: AssetUpdatePayload) => Promise<void>;

    /** Callback to upload a new asset file. */
    onUploadAsset: (file: File) => Promise<void>;

    /** The primary retailer profile for this case. */
    primaryRetailerData: RetailerProfileEntity | undefined;

    /** Whether available worksheet groups are currently being fetched. */
    isLoadingAvailableGroups: boolean;

    /** Vehicle VIN data for the case. */
    vehicleVinData: VinDetailEntity | undefined;

    /** DTC code data for the case. */
    dtcCodeData: DtcCodeEntity[];

    /** Whether DTC codes are currently being loaded. */
    isDtcLoading: { [dtcCode: string]: boolean };

    /** Whether the primary DTC code is currently being updated. */
    isPrimaryDtcUpdating: boolean;

    /** Error message related to DTC code operations. */
    dtcErrorMessage: string;

    /** Callback to add a DTC code. */
    onAddDtc: (dtc: DtcCodeEntity[]) => Promise<void>;

    /** Callback to delete a DTC code. */
    onDeleteDtc: (dtc: DtcCodeEntity) => Promise<void>;

    /** Callback to change the primary DTC code. */
    onPrimaryChange: (dtc: DtcCodeEntity) => Promise<void>;

    /** Callback to toggle freeze frame data for a DTC code. */
    onToggleFreezeFrame: (dtc: DtcCodeEntity) => Promise<void>;

    /** Validation results for draft worksheets. Keyed by worksheetId; true if valid. */
    worksheetValidationResults?: Record<string, boolean>;
};

/** Payload for updating an existing asset's metadata or content. */
type AssetUpdatePayload = {
    /** A replacement file blob, if re-uploading. */
    blob?: Blob;

    /** A new display name for the asset. */
    name?: string;

    /** A new note/description for the asset. */
    note?: string;
};

/** Height style for the main content area below the header. */
const contentAreaStyle = { height: '92%' };

/**
 * Subview for editing a draft Techline case. Renders the header, stepper, sidebar details,
 * and confirmation modals for save/submit/delete actions.
 *
 * @param props - see DraftCaseSubviewProps
 * @returns the draft case editing subview
 */
export function DraftCaseSubview({
    assetData,
    assetErrorMessage,
    availableGroupError,
    availableWorksheetGroupData,
    canEditDraft,
    caseDetails,
    caseNumber,
    draftStepperValidation,
    dtcCodeData,
    dtcErrorMessage,
    isDtcLoading,
    isLoadingAvailableGroups,
    isPrimaryDtcUpdating,
    onAddDtc,
    onAddTeamMember,
    onAssetErrorDismiss,
    onCaseDetailsChange,
    onDeleteAsset,
    onDeleteCase,
    onDeleteDraftWorksheetSubmission,
    onDeleteDtc,
    onFetchWorksheetTitles,
    onOpenWorksheet,
    onPrimaryChange,
    onRefreshAssets,
    onSaveCase,
    onSubmitCase,
    onToggleFreezeFrame,
    onUpdateAsset,
    onUploadAsset,
    primaryRetailerData,
    vehicleVinData,
    worksheetValidationResults,
}: DraftCaseSubviewProps) {
    // State
    const [errorMessage, setErrorMessage] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showBackModal, setShowBackModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Hooks
    const classes = useStyles();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Forwarded callback aliases
    const handleAddDtc = onAddDtc;
    const handleDeleteAsset = onDeleteAsset;
    const handleDeleteDraftWorksheetSubmission = onDeleteDraftWorksheetSubmission;
    const handleDeleteDtc = onDeleteDtc;
    const handleFetchWorksheetTitles = onFetchWorksheetTitles;
    const handlePrimaryChange = onPrimaryChange;
    const handleRefreshAssets = onRefreshAssets;
    const handleToggleFreezeFrame = onToggleFreezeFrame;
    const handleUpdateAsset = onUpdateAsset;
    const handleUploadAsset = onUploadAsset;

    // Derived state
    /** The initial stepper step parsed from the URL search params. */
    const stepParam = searchParams.get('step');
    const initialStep = stepParam != null ? Number.parseInt(stepParam, 10) : null;

    // Handlers

    /**
     * Shows a timed error message that automatically clears after 5 seconds.
     *
     * @param error - the error message to display
     */
    const handleShowErrorMessage = useCallback((error: string) => {
        setErrorMessage(error);
        setTimeout(() => {
            setErrorMessage('');
        }, 10000);
    }, []);

    /** Shows the back/unsaved-changes modal. */
    const handleBackButtonClick = useCallback(() => {
        setShowBackModal(true);
    }, []);

    /** Hides the back/unsaved-changes modal. */
    const handleBackModalClose = useCallback(() => {
        setShowBackModal(false);
    }, []);

    /** Clears the error message alert bar. */
    const handleErrorAlertClose = useCallback(() => {
        setErrorMessage('');
    }, []);

    /** Clears the success message alert bar. */
    const handleSuccessAlertClose = useCallback(() => {
        setSuccessMessage('');
    }, []);

    /** Exits without saving and navigates away. */
    const handleExitWithoutSavingButtonClick = useCallback(() => {
        signalDashboardAndCloseTab({
            retailerNumber: primaryRetailerData?.retailerNumber,
            caseNumber,
            fallbackNavigate: navigate,
        });
    }, [caseNumber, navigate, primaryRetailerData?.retailerNumber]);

    /** Opens the delete confirmation modal. */
    const handleDeleteButtonClick = useCallback(() => {
        setShowDeleteModal(true);
    }, []);

    /** Hides the delete confirmation modal. */
    const handleDeleteModalClose = useCallback(() => {
        setShowDeleteModal(false);
    }, []);

    /** Opens the submit confirmation modal. */
    const handleShowSubmitConfirmation = useCallback(() => {
        setShowSubmitConfirmation(true);
    }, []);

    /** Hides the submit confirmation modal. */
    const handleSubmitModalClose = useCallback(() => {
        setShowSubmitConfirmation(false);
    }, []);

    /**
     * Saves the draft and then exits; called from the back modal's "Save and exit" button.
     * On success, navigates away.
     */
    const handleSaveAndExit = useCallback(async () => {
        setIsSaving(true);
        try {
            //publish techline_case_save_start
            analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_SAVE_WORKSHEET_CLICKED, {
                caseNumber,
            });

            const success = await onSaveCase();
            if (success) {
                //publish techline_case_save_success
                analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_CASE_SAVE_SUCCESS, {
                    caseNumber,
                });
                signalDashboardAndCloseTab({
                    retailerNumber: primaryRetailerData?.retailerNumber,
                    caseNumber,
                    fallbackNavigate: navigate,
                });
            } else {
                // publish techline_case_save_failed
                analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_CASE_SAVE_FAILED, {
                    caseNumber,
                });
                handleShowErrorMessage('Failed to save draft case. Please try again.');
            }
        } catch (err) {
            logger.error(`Failed to save draft case [${caseNumber}]`, { caseNumber, error: err });
            handleShowErrorMessage('Error saving draft case: ' + err);
        } finally {
            setIsSaving(false);
            setShowBackModal(false);
        }
    }, [caseNumber, handleShowErrorMessage, navigate, onSaveCase, primaryRetailerData?.retailerNumber]);

    /**
     * Submits the case after user confirmation. On success, signals the
     * dashboard to refresh and navigates back to show the success message.
     */
    const handleSubmitConfirmed = useCallback(async () => {
        try {
            setIsSubmitting(true);
            setShowSubmitConfirmation(false);

            const success = await onSubmitCase();
            if (success) {
                signalDashboardAndCloseTab({
                    retailerNumber: primaryRetailerData?.retailerNumber,
                    caseNumber,
                    fallbackNavigate: navigate,
                    queryParams: `showSubmitCaseSuccessFor=${caseNumber}`,
                });
            } else {
                handleShowErrorMessage('Failed to submit case. Please try again.');
                setIsSubmitting(false);
            }
        } catch (err) {
            logger.error(`Failed to submit case [${caseNumber}]`, { caseNumber, error: err });
            handleShowErrorMessage('Error submitting case: ' + err);
            setIsSubmitting(false);
        }
    }, [caseNumber, handleShowErrorMessage, navigate, onSubmitCase, primaryRetailerData?.retailerNumber]);

    /**
     * Deletes the draft case after user confirmation. Navigates away on success.
     */
    const handleDeleteCase = useCallback(async () => {
        setIsDeleting(true);
        try {
            const success = await onDeleteCase();
            if (success) {
                signalDashboardAndCloseTab({
                    retailerNumber: primaryRetailerData?.retailerNumber,
                    caseNumber,
                    fallbackNavigate: navigate,
                });
            } else {
                handleShowErrorMessage('Failed to delete draft case. Please try again.');
            }
        } catch (err) {
            logger.error(`Failed to delete draft case [${caseNumber}]`, { caseNumber, error: err });
            handleShowErrorMessage('Failed to delete draft case. Please try again.');
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    }, [caseNumber, handleShowErrorMessage, navigate, onDeleteCase, primaryRetailerData?.retailerNumber]);

    return (
        <div className={`${classes.container} d-flex flex-column`}>
            <TechlineHeader
                caseDetails={caseDetails}
                caseNumber={caseNumber}
                disabledSave={!canEditDraft || isSaving}
                disabledSubmit={!isFormValid}
                isSaveLoading={isSaving}
                isSubmitLoading={isSubmitting}
                onBack={handleBackButtonClick}
                onDelete={handleDeleteButtonClick}
                onSaveAndExit={handleSaveAndExit}
                onSubmit={handleShowSubmitConfirmation}
                submitTooltipText={!isFormValid ? 'Please complete all required steps before submitting.' : undefined}
            />
            {errorMessage && (
                <div>
                    <AlertBar error onClose={handleErrorAlertClose} show={true}>
                        <Typography>ERROR: {errorMessage}</Typography>
                    </AlertBar>
                </div>
            )}
            {successMessage && (
                <div>
                    <AlertBar onClose={handleSuccessAlertClose} show={true} success>
                        <Typography>{successMessage}</Typography>
                    </AlertBar>
                </div>
            )}
            {assetErrorMessage && (
                <div>
                    <AlertBar error onClose={onAssetErrorDismiss} show={true}>
                        <Typography>{assetErrorMessage}</Typography>
                    </AlertBar>
                </div>
            )}
            <div className="d-flex flex-row" style={contentAreaStyle}>
                <div className={`${classes.centerContainer} pl-10 pr-10 pt-10`}>
                    <div className="d-flex flex-column h-100 w-100">
                        <TechlineDraftStepper
                            assetData={assetData}
                            caseDetails={caseDetails}
                            caseNumber={caseNumber}
                            draftStepperValidation={draftStepperValidation}
                            dtcCodeData={dtcCodeData}
                            dtcErrorMessage={dtcErrorMessage}
                            initialStep={initialStep}
                            isDtcLoading={isDtcLoading}
                            isLoadingGroups={isLoadingAvailableGroups}
                            isPrimaryDtcUpdating={isPrimaryDtcUpdating}
                            onAddDtc={handleAddDtc}
                            onCaseDetailsChange={onCaseDetailsChange}
                            onDeleteAsset={handleDeleteAsset}
                            onDeleteDraftWorksheetSubmission={handleDeleteDraftWorksheetSubmission}
                            onDeleteDtc={handleDeleteDtc}
                            onError={handleShowErrorMessage}
                            onFetchWorksheetTitles={handleFetchWorksheetTitles}
                            onOpenWorksheet={onOpenWorksheet}
                            onPrimaryChange={handlePrimaryChange}
                            onRefreshAssets={handleRefreshAssets}
                            onSubmit={handleShowSubmitConfirmation}
                            onSuccess={setSuccessMessage}
                            onToggleFreezeFrame={handleToggleFreezeFrame}
                            onUpdateAsset={handleUpdateAsset}
                            onUploadAsset={handleUploadAsset}
                            onValidationChange={setIsFormValid}
                            vehicleVinData={vehicleVinData}
                            worksheetGroupData={availableWorksheetGroupData}
                            worksheetGroupError={availableGroupError}
                            worksheetSubmissionGroups={caseDetails?.WorksheetSubmissionGroups}
                            worksheetValidationResults={worksheetValidationResults}
                        />
                    </div>
                </div>
                <DraftSidebar
                    caseDetails={caseDetails}
                    isAddMemberDisabled={!canEditDraft}
                    onAddTeamMember={onAddTeamMember}
                    onError={handleShowErrorMessage}
                    primaryRetailerData={primaryRetailerData}
                    vehicleVinData={vehicleVinData}
                />
            </div>
            <BackModal
                canEditDraft={canEditDraft}
                isSaving={isSaving}
                isVisible={showBackModal}
                onClose={handleBackModalClose}
                onExitWithoutSaving={handleExitWithoutSavingButtonClick}
                onSaveAndExit={handleSaveAndExit}
            />
            <SubmitCaseModal
                isSubmitting={isSubmitting}
                isVisible={showSubmitConfirmation}
                onClose={handleSubmitModalClose}
                onConfirm={handleSubmitConfirmed}
            />
            <DeleteCaseModal
                isDeleting={isDeleting}
                isVisible={showDeleteModal}
                onClose={handleDeleteModalClose}
                onConfirm={handleDeleteCase}
            />
        </div>
    );
}

const useStyles = createUseStyles({
    centerContainer: {
        flex: 1,
        overflowY: 'scroll',
        borderLeft: `1px solid ${colors.grayTwo}`,
        borderRight: `1px solid ${colors.grayTwo}`,
    },
    container: {
        height: '100%',
    },
});
